import { ApolloError } from "@apollo/client/errors";
import { from, of, Subject, Observable, timer } from "rxjs";
import { map, catchError, takeUntil, mergeMap } from "rxjs/operators";
import { ServerResponseStatus, CustomErrorCodes } from "./types.js";
import "immer";
import "moment-timezone";
import "react-intl";
import "@apollo/client";
import "graphql";
import "@opentelemetry/api";
import "../otel/TraceProvider.js";
import "react";
import "react-error-boundary";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import "@apollo/client/link/subscriptions";
import "@apollo/client/utilities";
import "cross-fetch";
import "graphql-ws";
import "../node_modules/moment/dist/moment.js";
import "rxjs/ajax";
class GraphQLService {
  client;
  _originalSubscriptionObservable = {};
  _unsubscribeNotifier = {};
  _messageLocalizer;
  constructor(client, messageLocalizer) {
    this.client = client;
    this._messageLocalizer = messageLocalizer;
  }
  mutate(mutation, variables) {
    return from(this.client.mutate({ mutation, variables })).pipe(
      map((res) => {
        let result;
        if (res.errors) {
          result = {
            requestPayload: { mutation, variables },
            responsePayload: res.data,
            ...this.parseMutationError(res)
          };
        } else {
          result = {
            status: ServerResponseStatus.Success,
            requestPayload: { mutation, variables },
            responsePayload: res.data
          };
        }
        return result;
      }),
      catchError((e) => {
        return of({
          requestPayload: { mutation, variables },
          responsePayload: null,
          ...this.parseApolloError(e)
        });
      })
    );
  }
  query(query, filter) {
    return from(this.client.query({ query, variables: filter })).pipe(
      map((res) => {
        let result;
        if (res.errors?.length) {
          result = {
            requestPayload: { query, variables: filter },
            responsePayload: res.data,
            ...this.parseQueryError(res)
          };
        } else {
          result = {
            status: ServerResponseStatus.Success,
            requestPayload: { query, variables: filter },
            responsePayload: res.data
          };
        }
        return result;
      }),
      catchError((e) => {
        return of({
          requestPayload: { query, variables: filter },
          responsePayload: null,
          ...this.parseQueryError(e)
        });
      })
    );
  }
  subscribeTemp(query, key, args) {
    this._unsubscribeNotifier[key] = new Subject();
    const subscribeWithRetry = new Observable((observer) => {
      const parseSuccess = (res) => {
        return {
          requestPayload: { query, variables: args },
          responsePayload: res?.data,
          errors: res?.errors?.map(this.parseGqlError),
          status: ServerResponseStatus.Success
        };
      };
      const parseError = (e) => {
        return {
          requestPayload: { query, variables: null },
          responsePayload: {},
          ...e.errors ? this.parseUnknownErrors(e) : this.parseApolloError(e)
        };
      };
      const subscribe = () => {
        const observable = this.subscribeGql(query, key, args);
        const subscribeTimeout = 500;
        return timer(subscribeTimeout).pipe(mergeMap(() => observable)).subscribe(
          (res) => {
            observer.next(parseSuccess(res));
          },
          (err) => {
            observer.next(parseError(err));
            this.clearCache(key);
          }
        );
      };
      const subscription = subscribe();
      return () => {
        subscription.unsubscribe();
      };
    });
    return subscribeWithRetry.pipe(
      map((e) => e),
      takeUntil(this._unsubscribeNotifier[key])
    );
  }
  unsubscribe(keys) {
    const unsubscribeNotifiers = keys.map((key) => this._unsubscribeNotifier[key]);
    keys.forEach((k) => this.clearCache(k));
    unsubscribeNotifiers.forEach((notifier) => {
      if (notifier) {
        notifier.next(null);
        notifier.unsubscribe();
      }
    });
    return of({
      requestPayload: null,
      responsePayload: null,
      status: ServerResponseStatus.Success
    });
  }
  parseQueryError(error) {
    return error instanceof ApolloError ? this.parseApolloError(error) : this.parseGraphQLErrors(error);
  }
  parseMutationError(errorResult) {
    return {
      status: ServerResponseStatus.Failed,
      errors: errorResult?.errors?.map(this.parseGqlError)
    };
  }
  parseApolloError(e) {
    const result = {
      status: ServerResponseStatus.Failed,
      message: e.message,
      errors: []
    };
    if (e.networkError) {
      const error = this.parseNetworkError(e.networkError);
      result.errors.push(error);
      result.statusCode = error.statusCode;
    }
    if (e.graphQLErrors?.length) {
      result.errors.push(...e.graphQLErrors.map(this.parseGqlError));
    }
    return result;
  }
  parseNetworkError(networkError) {
    let result;
    if (networkError.statusCode) {
      const code = networkError.statusCode.toString();
      const isDefaultMessage = !this._messageLocalizer.isLocalizationExist(code);
      result = {
        code: networkError.statusCode.toString(),
        message: isDefaultMessage ? networkError.message : this._messageLocalizer.getMessageDescriptor(code).defaultMessage,
        values: [],
        statusCode: networkError.statusCode
      };
    } else {
      result = {
        code: `${CustomErrorCodes.NetworkRequestFailed}`,
        message: networkError.message,
        values: []
      };
    }
    return result;
  }
  parseGraphQLErrors(e) {
    const result = {
      status: ServerResponseStatus.Failed,
      message: e?.errors?.length ? e.errors[0].message : "",
      errors: []
    };
    if (e.error) {
      result.statusCode = this.parseApolloError(e.error)?.statusCode;
    }
    if (e.errors?.length) {
      result.errors.push(...e.errors.map(this.parseGqlError));
    }
    return result;
  }
  parseGqlError(error) {
    let jsonError;
    try {
      jsonError = JSON.parse(error.message);
    } catch {
      jsonError = {
        error: {
          message: error?.message,
          //TODO: [BO-2975] Remove error?.name after switch to ariadne
          type: error?.name ?? error?.extensions?.errorType ?? "400",
          data: error?.extensions?.data,
          validation_errors: (error?.extensions?.errorInfo ?? {}).validation_errors
        }
      };
    }
    return {
      message: jsonError?.error?.message,
      code: jsonError?.error?.type,
      values: [],
      data: jsonError?.error?.data,
      validation_errors: jsonError?.error?.validation_errors
    };
  }
  clearCache(key) {
    const subscription = this._originalSubscriptionObservable[key];
    if (subscription) {
      delete this._originalSubscriptionObservable[key];
      delete this._unsubscribeNotifier[key];
    }
  }
  subscribeGql(query, key, args) {
    if (!this._originalSubscriptionObservable[key]) {
      this._originalSubscriptionObservable[key] = this.client.subscribeRx({ query, variables: args, fetchPolicy: "no-cache" });
    }
    return this._originalSubscriptionObservable[key];
  }
  parseUnknownErrors(e) {
    const result = {
      status: ServerResponseStatus.Failed,
      message: e?.errors?.length ? e.errors[0].message : "",
      errors: []
    };
    if (e.errors?.length) {
      result.errors.push(...e.errors.map(this.parseUnknownError));
    }
    return result;
  }
  parseUnknownError(error) {
    return {
      code: error.message,
      message: error.message,
      values: []
    };
  }
}
export {
  GraphQLService
};
//# sourceMappingURL=gqlService.js.map
